/**
 * Unified print/PDF template so every printable sheet (brackets, calendar,
 * results, ranking) shares the same branded layout. The audit found three
 * hand-rolled print builders with diverging formats, plus a hardcoded
 * `zoom: 0.38` that printed elimination draws tiny regardless of size —
 * this template measures the content and fits it to the page instead.
 */

export interface PrintSheetOptions {
  /** Window/document title (also the suggested PDF filename). */
  title: string;
  /** Small label above the heading, e.g. "Bracket oficial de playoffs". */
  eyebrow: string;
  /** Main heading, normally the tournament name. */
  heading: string;
  /** Secondary line, normally the category / section / week. */
  subtitle?: string;
  /** Pre-built HTML for the sheet body. */
  bodyHtml: string;
  /** Extra CSS appended after the template styles. */
  extraCss?: string;
  /** Landscape for wide content (brackets, calendar). Default portrait. */
  landscape?: boolean;
  /**
   * Reserve the top-right hero cell for the tournament organizer's logo
   * (placeholder box when `organizerLogoUrl` is empty). When set, the
   * "Generado …" line moves to a small footer.
   */
  organizerSlot?: boolean;
  /** Resolved organizer/tournament logo URL for the top-right slot. */
  organizerLogoUrl?: string | null;
  /**
   * When true, the body's first child is scaled down to fit one letter page
   * by BOTH width and height (never scaled up), measured against the fixed
   * printable area — not the window. Use for fixed-size content like bracket
   * trees. Leave false for reflowable content (tables) that may span pages.
   */
  fitToPage?: boolean;
  /** Copy existing stylesheets into the print window (for cloned DOM). */
  includeAppStyles?: boolean;
}

export function openPrintWindow(options: PrintSheetOptions): void {
  const printWindow = window.open("", "_blank", "width=1280,height=900");
  if (!printWindow) {
    throw new Error(
      "El navegador bloqueó la ventana de impresión. Permite pop-ups para Torneos123 y vuelve a intentar.",
    );
  }

  const appStyles = options.includeAppStyles ? collectAppStyleMarkup() : "";

  const generatedAt = new Intl.DateTimeFormat("es-MX", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date());

  // Printable area of a US-letter page (minus @page margin) in CSS px @96dpi.
  // These are FIXED — the fit-to-page scaler measures against them, never the
  // on-screen window, so a phone print tab (~375px) no longer shrinks fixed
  // content (brackets) to phone width and prints it tiny / cut off.
  const MARGIN_MM = 8;
  const PX_PER_MM = 96 / 25.4;
  const pageWmm = options.landscape ? 279.4 : 215.9;
  const pageHmm = options.landscape ? 215.9 : 279.4;
  const contentWpx = Math.floor((pageWmm - 2 * MARGIN_MM) * PX_PER_MM);
  const contentHpx = Math.floor((pageHmm - 2 * MARGIN_MM) * PX_PER_MM);

  printWindow.document.write(`<!doctype html>
<html data-theme="light">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(options.title)}</title>
  ${appStyles}
  <style>
    @page { size: letter ${options.landscape ? "landscape" : "portrait"}; margin: ${MARGIN_MM}mm; }
    body.print-sheet-body { margin: 0; padding: 16px; color: #10220f; background: #ffffff; font-family: "Inter", "Segoe UI", Arial, sans-serif; }
    /* Preview sheet is pinned to the printable page width so the on-screen
       layout (and the measured hero height) match the printed page exactly. */
    .print-sheet { box-sizing: border-box; width: ${contentWpx}px; max-width: 100%; margin: 0 auto; padding: 18px; background: #ffffff; }
    .print-sheet-hero { display: grid; grid-template-columns: 170px minmax(0, 1fr) auto; gap: 16px; align-items: center; margin-bottom: 16px; padding: 14px; border: 1px solid #d8e7cf; border-radius: 16px; background: linear-gradient(135deg, #f4faee, #ffffff); }
    .print-sheet-hero__brand { padding: 8px; border-radius: 12px; background: #ffffff; }
    .print-sheet-hero__brand img { display: block; width: 100%; max-height: 64px; object-fit: contain; }
    .print-sheet-hero__eyebrow { margin: 0 0 2px; font-size: 0.72rem; font-weight: 800; letter-spacing: 0.14em; text-transform: uppercase; color: #4c7a36; }
    .print-sheet-hero h1 { margin: 0; font-size: 1.55rem; line-height: 1.15; color: #10220f; }
    .print-sheet-hero__subtitle { margin: 4px 0 0; font-weight: 700; color: #2e4d22; }
    .print-sheet-hero__meta { font-size: 0.78rem; color: #57704c; text-align: right; }
    .print-sheet-hero__org { justify-self: end; display: flex; align-items: center; justify-content: center; width: 168px; height: 66px; padding: 6px 10px; border-radius: 12px; background: #ffffff; border: 1px solid #d8e7cf; }
    .print-sheet-hero__org img { display: block; max-width: 100%; max-height: 52px; object-fit: contain; }
    .print-sheet-hero__org-empty { font-size: 0.6rem; line-height: 1.3; letter-spacing: 0.09em; text-transform: uppercase; text-align: center; color: #a7b89a; font-weight: 800; }
    .print-sheet-foot { margin-top: 14px; padding-top: 8px; border-top: 1px solid #e4eedb; font-size: 0.66rem; color: #809772; text-align: right; }
    ${options.fitToPage ? `
    /* Compact hero for fitted sheets: one-line title, minimal chrome, so the
       hero steals as little page height as possible from the bracket. */
    .print-sheet-hero { grid-template-columns: 118px minmax(0, 1fr) auto; gap: 10px; margin-bottom: 10px; padding: 7px 12px; border-radius: 12px; }
    .print-sheet-hero__brand { padding: 3px 6px; border-radius: 9px; }
    .print-sheet-hero__brand img { max-height: 34px; }
    .print-sheet-hero__eyebrow { margin: 0; font-size: 0.58rem; letter-spacing: 0.12em; }
    .print-sheet-hero h1 { font-size: 1rem; line-height: 1.12; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .print-sheet-hero__subtitle { margin: 1px 0 0; font-size: 0.72rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .print-sheet-hero__meta { font-size: 0.62rem; }
    .print-sheet-hero__org { width: 128px; height: 46px; padding: 3px 8px; border-radius: 9px; }
    .print-sheet-hero__org img { max-height: 38px; }
    .print-sheet-hero__org-empty { font-size: 0.5rem; }
    .print-sheet-foot { margin-top: 8px; padding-top: 5px; font-size: 0.58rem; }
    ` : ""}
    .print-sheet-content { background: #ffffff; }
    .print-sheet table { width: 100%; border-collapse: collapse; font-size: 0.86rem; }
    .print-sheet th, .print-sheet td { padding: 6px 8px; border: 1px solid #d8e7cf; text-align: left; color: #10220f; }
    .print-sheet th { background: #eef6e6; font-size: 0.74rem; text-transform: uppercase; letter-spacing: 0.06em; }
    .print-sheet tbody tr:nth-child(even) td { background: #f8fcf4; }
    .print-sheet h2.print-section-title { margin: 18px 0 8px; font-size: 1.05rem; color: #1d3414; border-bottom: 2px solid #b9d89e; padding-bottom: 4px; }
    .print-sheet .print-muted { color: #6b7f60; }
    /* Eduardo 2026-10-05: quién descansa la jornada, debajo de su tabla. */
    .print-sheet .print-rest-line { margin: 6px 0 0; font-size: 0.82rem; font-weight: 700; color: #2e4d22; }
    /* Fechas de la jornada por categoría cuando no coinciden entre ellas. */
    .print-sheet .print-section-ranges { margin: -2px 0 8px; font-size: 0.82rem; font-weight: 700; color: #2e4d22; }
    .print-sheet-toolbar { position: sticky; top: 0; z-index: 20; display: flex; gap: 0.5rem; justify-content: flex-end; margin: 0 0 0.6rem; padding: 0.45rem; border: 1px solid rgba(31, 55, 22, 0.14); border-radius: 0.7rem; background: rgba(255, 255, 255, 0.94); }
    .print-sheet-toolbar button { border: 1px solid rgba(31, 55, 22, 0.18); border-radius: 999px; padding: 0.42rem 0.75rem; color: #12200f; background: #f7fbf2; font: inherit; font-weight: 800; cursor: pointer; }
    @media print {
      .print-sheet-toolbar { display: none !important; }
      body.print-sheet-body { padding: 0; }
      .print-sheet { width: 100%; padding: 0; max-width: none; }
      .print-sheet-hero { break-inside: avoid; page-break-inside: avoid; }
      .print-sheet h2.print-section-title { break-after: avoid; }
      .print-sheet tr { break-inside: avoid; }
      /* A fitted (scaled) block is pre-sized to one page — keep it whole. */
      .print-sheet-content--fit { break-inside: avoid; page-break-inside: avoid; overflow: visible !important; }
      .print-sheet-content--fit > * { break-inside: avoid; page-break-inside: avoid; }
    }
    ${options.extraCss ?? ""}
  </style>
</head>
<body class="print-sheet-body">
  <div class="print-sheet-toolbar">
    <button type="button" onclick="window.close()">Regresar</button>
    <button type="button" onclick="window.print()">Imprimir / PDF</button>
  </div>
  <main class="print-sheet">
    <header class="print-sheet-hero">
      <div class="print-sheet-hero__brand">
        <img src="${import.meta.env.BASE_URL}torneos123/logo-torneos123-header-on-light.png" alt="Torneos123" />
      </div>
      <div>
        <p class="print-sheet-hero__eyebrow">${escapeHtml(options.eyebrow)}</p>
        <h1>${escapeHtml(options.heading)}</h1>
        ${options.subtitle ? `<p class="print-sheet-hero__subtitle">${escapeHtml(options.subtitle)}</p>` : ""}
      </div>
      ${options.organizerSlot
        ? `<div class="print-sheet-hero__org">${
            options.organizerLogoUrl
              ? `<img src="${escapeHtml(options.organizerLogoUrl)}" alt="Logo del organizador" />`
              : `<span class="print-sheet-hero__org-empty">Logo del<br/>organizador</span>`
          }</div>`
        : `<small class="print-sheet-hero__meta">Generado ${generatedAt}<br/>torneos123.com</small>`}
    </header>
    <section class="print-sheet-content${options.fitToPage ? " print-sheet-content--fit" : ""}">
      ${options.bodyHtml}
    </section>
    ${options.organizerSlot ? `<footer class="print-sheet-foot">Generado ${generatedAt} · torneos123.com</footer>` : ""}
  </main>
  <script>
    async function waitForPrintAssets() {
      const styleSheets = Array.from(document.querySelectorAll('link[rel="stylesheet"]')).map((link) => new Promise((resolve) => {
        if (link.sheet) return resolve(true);
        link.addEventListener('load', () => resolve(true), { once: true });
        link.addEventListener('error', () => resolve(false), { once: true });
        setTimeout(() => resolve(false), 1800);
      }));
      const images = Array.from(document.images).map((img) => img.complete ? Promise.resolve(true) : new Promise((resolve) => {
        img.addEventListener('load', () => resolve(true), { once: true });
        img.addEventListener('error', () => resolve(false), { once: true });
        setTimeout(() => resolve(false), 1800);
      }));
      await Promise.all([...styleSheets, ...images]);
      if (document.fonts?.ready) await document.fonts.ready;
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    }
    function fitContentToSheet() {
      const frame = document.querySelector('.print-sheet-content--fit');
      if (!frame) return;
      const inner = frame.firstElementChild;
      if (!inner) return;
      // Fixed printable box (letter, minus margins) — independent of the
      // window/screen so mobile and desktop scale identically.
      const CONTENT_W = ${contentWpx};
      const CONTENT_H = ${contentHpx};
      // The first child can be a viewport wrapper whose own scrollWidth stays
      // at the phone/tab width while a fixed bracket overflows inside it. Use
      // the actual bracket canvas as an additional measurement source so the
      // final rounds are included in the one-page scale.
      inner.style.maxWidth = 'none';
      inner.style.overflow = 'visible';
      const fixedCanvas = inner.querySelector('.bracket-pro, .elimination-draw-sheet');
      const naturalW = Math.max(
        inner.scrollWidth,
        inner.offsetWidth,
        fixedCanvas ? fixedCanvas.scrollWidth : 0,
        fixedCanvas ? fixedCanvas.offsetWidth : 0,
      );
      const naturalH = Math.max(
        inner.scrollHeight,
        inner.offsetHeight,
        fixedCanvas ? fixedCanvas.scrollHeight : 0,
        fixedCanvas ? fixedCanvas.offsetHeight : 0,
      );
      if (naturalW <= 0 || naturalH <= 0) return;
      function apply(k) {
        // CSS zoom participates in print layout; transform:scale can look right
        // on screen while the print engine paginates the element at its original
        // height, creating a mostly blank second page.
        inner.style.transform = 'none';
        inner.style.zoom = String(k);
        inner.style.position = 'relative';
        inner.style.inset = 'auto';
        inner.style.width = naturalW + 'px';
        inner.style.height = naturalH + 'px';
        frame.style.position = 'relative';
        frame.style.width = Math.ceil(naturalW * k) + 'px';
        frame.style.height = Math.ceil(naturalH * k) + 'px';
        frame.style.margin = '0 auto';
        frame.style.maxWidth = 'none';
        frame.style.overflow = 'visible';
      }
      const hero = document.querySelector('.print-sheet-hero');
      const heroH = hero ? Math.ceil(hero.getBoundingClientRect().height) : 0;
      const foot = document.querySelector('.print-sheet-foot');
      const footH = foot ? Math.ceil(foot.getBoundingClientRect().height) : 0;
      const availW = CONTENT_W;
      const availH = Math.max(200, CONTENT_H - heroH - footH - 40);
      const rawScale = Math.min(1, availW / naturalW, availH / naturalH);
      // Print engines (including Safari and Chromium) can round scaled boxes
      // differently and spill the last few pixels onto a second page. Keep a
      // small safety margin only when the draw actually needs scaling.
      let k = Math.max(0.02, rawScale < 1 ? rawScale * 0.92 : 1);
      apply(k);
      // Self-correcting passes: measure the REAL total sheet height (hero +
      // margins + fitted block + footer). If it still exceeds one page, the
      // print engine's break-inside:avoid would push the whole bracket to
      // page 2 — shrink a bit more until everything truly fits page 1.
      const sheet = document.querySelector('.print-sheet');
      // The preview sheet has padding that @media print removes — exclude it.
      let sheetPadV = 0;
      if (sheet) {
        const cs = window.getComputedStyle(sheet);
        sheetPadV = (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
      }
      for (let i = 0; i < 4 && sheet; i++) {
        const total = sheet.scrollHeight - sheetPadV;
        if (total <= CONTENT_H) break;
        const frameH = Math.max(1, frame.getBoundingClientRect().height);
        const targetH = Math.max(120, frameH - (total - CONTENT_H) - 6);
        const next = Math.max(0.02, k * (targetH / frameH));
        if (next >= k) break;
        k = next;
        apply(k);
      }
    }
    window.addEventListener('load', () => waitForPrintAssets().then(() => {
      // Expand any collapsed <details> (e.g. Round Robin groups) so they print.
      document.querySelectorAll('details').forEach((d) => { d.open = true; });
      fitContentToSheet();
      setTimeout(() => window.print(), 250);
    }));
  <\/script>
</body>
</html>`);
  printWindow.document.close();
}

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Serialize the app's stylesheets into an inline <style> block.
 *
 * Print windows used to copy `<link rel="stylesheet">` hrefs, but a deploy
 * with `rsync --delete` removes the old hashed CSS file: any tab opened
 * before the deploy then prints completely unstyled (the link 404s). The
 * styles a tab is already using live in memory — inlining them makes the
 * print window self-contained and deploy-proof.
 */
export function collectAppStyleMarkup(): string {
  const chunks: string[] = [];
  for (const sheet of Array.from(document.styleSheets)) {
    try {
      const rules = sheet.cssRules;
      if (!rules) continue;
      chunks.push(
        Array.from(rules)
          .map((rule) => rule.cssText)
          .join("\n"),
      );
    } catch {
      // Cross-origin stylesheet: cssRules is unreadable; fall back to the
      // link and accept that it may not load.
      const href = (sheet as CSSStyleSheet).href;
      if (href) chunks.push(`@import url("${href}");`);
    }
  }
  return `<style>\n${chunks.join("\n")}\n</style>`;
}
